/**
 * Pure localStorage helpers for the Studio "My Pitches" list cache.
 * Mirrors the website's stale-while-revalidate seed pattern — the SPA
 * equivalent of `window.__feedSeed` is a persisted snapshot keyed by
 * the Appwrite auth user id.
 */

import type { PitchRating } from "../lib/appwrite";

export const CACHE_VERSION = 1 as const;
export const MAX_CACHED_PITCHES_PER_TAB = 200;

export interface DashboardPitch {
  $id: string;
  title?: string;
  slug?: string;
  seoSlug?: string;
  hpCode?: string;
  status?: string;
  visibility?: string;
  archivedAt?: string | number;
  expiresAt?: string | number;
  bunnyVideoId?: string | null;
  primaryVideoId?: string | null;
  thumbnailFileId?: string | null;
  ogImageFileId?: string | null;
  /** Pre-resolved CDN URL (cache-only enrichment). */
  thumbnailUrl?: string | null;
  /** Pre-resolved CDN URL (cache-only enrichment). */
  ogImageUrl?: string | null;
  /** Cached HonestScore (cache-only enrichment). */
  honestScore?: number | null;
  /** Cached critic count (cache-only enrichment). */
  criticCount?: number;
  $updatedAt?: string;
}

export interface CachedPitchesSnapshot {
  version: typeof CACHE_VERSION;
  savedAt: string;
  active: DashboardPitch[];
  archived: DashboardPitch[];
  ratings: Record<string, PitchRating>;
  assetUrls: Record<string, string>;
}

export interface LoadedCachedPitches {
  active: DashboardPitch[];
  archived: DashboardPitch[];
  ratings: Map<string, PitchRating>;
  assetUrls: Map<string, string>;
  savedAt: string;
}

export function cacheKeyForUser(creatorUserId: string): string {
  return `hp:studio:pitches:v${CACHE_VERSION}:${creatorUserId}`;
}

function pitchSortKey(p: DashboardPitch): number {
  const raw = p.$updatedAt ?? p.archivedAt ?? 0;
  if (typeof raw === "number") return raw;
  const parsed = Date.parse(String(raw));
  return Number.isFinite(parsed) ? parsed : 0;
}

function capPitchList(list: DashboardPitch[]): DashboardPitch[] {
  if (list.length <= MAX_CACHED_PITCHES_PER_TAB) return list;
  return [...list]
    .sort((a, b) => pitchSortKey(b) - pitchSortKey(a))
    .slice(0, MAX_CACHED_PITCHES_PER_TAB);
}

export function toDashboardPitch(doc: Record<string, unknown>): DashboardPitch {
  const pitch: DashboardPitch = {
    $id: String(doc.$id ?? ""),
  };
  if (typeof doc.title === "string") pitch.title = doc.title;
  if (typeof doc.slug === "string") pitch.slug = doc.slug;
  if (typeof doc.seoSlug === "string") pitch.seoSlug = doc.seoSlug;
  if (typeof doc.hpCode === "string") pitch.hpCode = doc.hpCode;
  if (typeof doc.status === "string") pitch.status = doc.status;
  if (typeof doc.visibility === "string") pitch.visibility = doc.visibility;
  if (typeof doc.archivedAt === "string" || typeof doc.archivedAt === "number") {
    pitch.archivedAt = doc.archivedAt as string | number;
  }
  if (typeof doc.thumbnailFileId === "string") {
    pitch.thumbnailFileId = doc.thumbnailFileId;
  }
  if (typeof doc.ogImageFileId === "string") {
    pitch.ogImageFileId = doc.ogImageFileId;
  }
  if (typeof doc.$updatedAt === "string") pitch.$updatedAt = doc.$updatedAt;
  return pitch;
}

/**
 * Additive merge keyed by `$id` (always present on studio rows).
 * Prefer incoming fields; preserve cached enrichments the fetch
 * did not set (thumbnail URLs, honestScore).
 */
export function mergePitches(
  existing: DashboardPitch[],
  incoming: DashboardPitch[],
): DashboardPitch[] {
  if (incoming.length === 0) return existing;
  const incomingById = new Map<string, DashboardPitch>();
  for (const p of incoming) {
    if (p.$id) incomingById.set(p.$id, p);
  }
  const updated = existing.map((p) => {
    if (!p.$id) return p;
    const fresh = incomingById.get(p.$id);
    if (!fresh) return p;
    const merged: DashboardPitch = { ...fresh };
    if (!merged.thumbnailUrl && p.thumbnailUrl) merged.thumbnailUrl = p.thumbnailUrl;
    if (!merged.ogImageUrl && p.ogImageUrl) merged.ogImageUrl = p.ogImageUrl;
    if (merged.honestScore == null && p.honestScore != null) {
      merged.honestScore = p.honestScore;
    }
    if (merged.criticCount == null && p.criticCount != null) {
      merged.criticCount = p.criticCount;
    }
    return merged;
  });
  const existingIds = new Set(updated.map((p) => p.$id).filter(Boolean));
  const added = incoming.filter((p) => p.$id && !existingIds.has(p.$id));
  return [...added, ...updated];
}

export function loadCachedPitches(creatorUserId: string): LoadedCachedPitches | null {
  if (!creatorUserId || typeof localStorage === "undefined") return null;
  const key = cacheKeyForUser(creatorUserId);
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<CachedPitchesSnapshot>;
    if (parsed.version !== CACHE_VERSION) {
      localStorage.removeItem(key);
      return null;
    }
    if (!Array.isArray(parsed.active) || !Array.isArray(parsed.archived)) {
      localStorage.removeItem(key);
      return null;
    }
    const ratingsObj = parsed.ratings && typeof parsed.ratings === "object"
      ? parsed.ratings
      : {};
    const assetObj = parsed.assetUrls && typeof parsed.assetUrls === "object"
      ? parsed.assetUrls
      : {};
    return {
      active: parsed.active as DashboardPitch[],
      archived: parsed.archived as DashboardPitch[],
      ratings: new Map(Object.entries(ratingsObj)),
      assetUrls: new Map(Object.entries(assetObj)),
      savedAt: typeof parsed.savedAt === "string" ? parsed.savedAt : "",
    };
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

export function saveCachedPitches(
  creatorUserId: string,
  active: DashboardPitch[],
  archived: DashboardPitch[],
  ratings: Map<string, PitchRating>,
  assetUrls: Map<string, string>,
): void {
  if (!creatorUserId || typeof localStorage === "undefined") return;
  const envelope: CachedPitchesSnapshot = {
    version: CACHE_VERSION,
    savedAt: new Date().toISOString(),
    active: capPitchList(active),
    archived: capPitchList(archived),
    ratings: Object.fromEntries(ratings),
    assetUrls: Object.fromEntries(assetUrls),
  };
  try {
    localStorage.setItem(cacheKeyForUser(creatorUserId), JSON.stringify(envelope));
  } catch {
    // Safari private mode / quota exceeded — non-fatal.
  }
}

export function clearCachedPitches(creatorUserId: string | null | undefined): void {
  if (!creatorUserId || typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(cacheKeyForUser(creatorUserId));
  } catch {
    // non-fatal
  }
}
